<script setup>
import { useUserStore } from "@/store/user-store";
import Sectionheader from "./Sectionheader.vue";
import { ref, shallowRef, watch, watchEffect, onMounted, onUnmounted } from "vue";
import AnButton from "../common/AnButton.vue";

const store = useUserStore();
const listItems = shallowRef(null);
const subtabs = ref(null);
const tabContentRef = ref(null);

const props = defineProps({
  data: { type: Object, default: {} },
});

// Loading messages with icons
const loadingMessages = [
  {
    icon: 'pi-chart-line',
    title: 'Setting up your dashboard...',
    message: 'Please wait while we prepare your personalized financial dashboard'
  },
  {
    icon: 'pi-chart-bar',
    title: 'Analyzing your financial data...',
    message: 'We are evaluating your goals and current financial status to recommend optimal actions'
  },
  {
    icon: 'pi-wallet',
    title: 'Loading your portfolio...',
    message: 'Fetching your assets, liabilities, income & expenses for comprehensive insights'
  },
  {
    icon: 'pi-bolt',
    title: 'Generating insights...',
    message: 'Our analytics engine is processing your data to deliver actionable recommendations'
  }
];

const currentMessageIndex = ref(0);
let messageInterval = null;

onMounted(() => {
  // Rotate messages every 3 seconds
  if (store.isProfileCheckLoading) {
    messageInterval = setInterval(() => {
      currentMessageIndex.value = (currentMessageIndex.value + 1) % loadingMessages.length;
    }, 3000);
  }
});

onUnmounted(() => {
  if (messageInterval) {
    clearInterval(messageInterval);
  }
});

watchEffect(() => {
  listItems.value = props.data.listData;
  // Check if the current userTab exists in listData before accessing tabs
  const currentTabData = props.data.listData[store.userTab];
  subtabs.value = currentTabData?.tabs && [
    ...currentTabData.tabs,
  ];

  // Handle message rotation based on loading state
  if (store.isProfileCheckLoading && !messageInterval) {
    messageInterval = setInterval(() => {
      currentMessageIndex.value = (currentMessageIndex.value + 1) % loadingMessages.length;
    }, 3000);
  } else if (!store.isProfileCheckLoading && messageInterval) {
    clearInterval(messageInterval);
    messageInterval = null;
  }
});

const handleTabChange = (tab) => {
  subtabs.value = subtabs.value.map((item) => ({
    ...item,
    selected: item.id === tab,
  }));
};

watch(subtabs, () => {
  tabContentRef.value?.scrollTo({ top: 0, behavior: "smooth" });
});
</script>

<template>
  <div
    ref="tabContentRef"
    class="w-100 z-1 section-wrapper p-20 d-flex flex-direction-column position-relative bg-white flex-1"
  >
    <!-- Profile Check Loading State -->
    <div v-if="store.isProfileCheckLoading" class="loading-view">
      <div class="loading-icon-wrapper">
        <i :class="`pi ${loadingMessages[currentMessageIndex].icon} loading-icon`"></i>
      </div>
      <h3 class="loading-title">{{ loadingMessages[currentMessageIndex].title }}</h3>
      <p class="loading-message">{{ loadingMessages[currentMessageIndex].message }}</p>
      <div class="loading-dots">
        <span
          v-for="(_, index) in loadingMessages"
          :key="index"
          class="dot"
          :class="{ active: index === currentMessageIndex }"
        ></span>
      </div>
    </div>

    <!-- Normal Content -->
    <template v-else>
      <!--<template class="desktop">-->
        <Sectionheader ref="sectionHeaderRef">
          <template #actions>
            <slot name="header-actions"></slot>
          </template>
        </Sectionheader>
      <!--</template>-->
      <div class="d-flex flex-direction-column">
        <section
          v-if="listItems && listItems[store.userTab] && listItems[store.userTab].tabs?.length > 0"
          class="d-flex gap-10 mt-25"
        >
          <div v-for="item in subtabs" :key="item.id">
            <AnButton
              :label="item.label"
              :variant="item.selected ? 'primary' : 'tab'"
              @click="() => handleTabChange(item.id)"
            />
          </div>
        </section>
      </div>
      <!-- Debug view removed -->
      <component
        v-if="listItems && listItems[store.userTab] && !listItems[store.userTab].disabled"
        :is="listItems[store.userTab].component"
        v-bind="listItems[store.userTab]?.tabs ? { tabs: subtabs } : {}"
      />
      <div v-else-if="listItems && listItems[store.userTab] && listItems[store.userTab].disabled" class="disabled-view">
        <i class="pi pi-lock disabled-icon"></i>
        <h3>This Feature is Currently Locked</h3>
        <p>Please complete your payment to unlock this feature.</p>
      </div>
    </template>
  </div>
</template>

<style scoped>
.section-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 20px;
  height: calc(100vh - 40px);
  /* Custom scrollbar styling to match the desired design */
  scrollbar-width: thin;
  scrollbar-color: #ced4da transparent;
}

.section-wrapper::-webkit-scrollbar {
  width: 8px;
}

.section-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.section-wrapper::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #e0e0e0, #c0c0c0);
  border-radius: 10px;
  border: 2px solid transparent;
}

.section-wrapper::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #c0c0c0, #a0a0a0);
}

.debug-info {
  padding: 20px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  margin-top: 20px;
}

.debug-info p {
  margin: 8px 0;
  color: #856404;
  font-family: monospace;
}

.disabled-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  min-height: 400px;
  text-align: center;
}

.disabled-icon {
  font-size: 4rem;
  color: #9ca3af;
}

.disabled-view h3 {
  font-size: 1.5rem;
  color: #4b5563;
  margin: 0;
}

.disabled-view p {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  max-width: 400px;
}

.loading-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 20px;
  min-height: 400px;
  text-align: center;
}

.loading-icon-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(21, 87, 36, 0.15);
  animation: pulse 2s ease-in-out infinite;
}

.loading-icon {
  font-size: 3rem;
  color: #155724;
  animation: fadeInUp 0.5s ease-out;
}

.loading-title {
  font-size: 1.5rem;
  color: #155724;
  font-weight: 600;
  margin: 0;
  animation: fadeInUp 0.6s ease-out;
}

.loading-message {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  max-width: 500px;
  line-height: 1.6;
  animation: fadeInUp 0.7s ease-out;
}

.loading-dots {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.loading-dots .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d1d5db;
  transition: all 0.3s ease;
}

.loading-dots .dot.active {
  background: #155724;
  transform: scale(1.2);
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(21, 87, 36, 0.15);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(21, 87, 36, 0.25);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1200px) {
  .section-wrapper {
    border-radius: 0;
  }
}

@media (max-width: 768px) {
  .loading-view {
    padding: 40px 16px;
    gap: 16px;
    min-height: 300px;
  }

  .loading-icon-wrapper {
    width: 80px;
    height: 80px;
  }

  .loading-icon {
    font-size: 2.5rem;
  }

  .loading-title {
    font-size: 1.25rem;
  }

  .loading-message {
    font-size: 0.9rem;
    max-width: 400px;
  }

  .loading-dots .dot {
    width: 8px;
    height: 8px;
  }

  .disabled-view {
    padding: 40px 16px;
  }

  .disabled-icon {
    font-size: 3rem;
  }

  .disabled-view h3 {
    font-size: 1.25rem;
  }

  .disabled-view p {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .loading-view {
    padding: 32px 12px;
    gap: 12px;
    min-height: 250px;
  }

  .loading-icon-wrapper {
    width: 70px;
    height: 70px;
  }

  .loading-icon {
    font-size: 2rem;
  }

  .loading-title {
    font-size: 1.1rem;
  }

  .loading-message {
    font-size: 0.85rem;
    max-width: 320px;
    line-height: 1.5;
  }

  .loading-dots {
    gap: 6px;
    margin-top: 4px;
  }

  .loading-dots .dot {
    width: 7px;
    height: 7px;
  }

  .disabled-view {
    padding: 32px 12px;
    gap: 12px;
  }

  .disabled-icon {
    font-size: 2.5rem;
  }

  .disabled-view h3 {
    font-size: 1.1rem;
  }

  .disabled-view p {
    font-size: 0.85rem;
    max-width: 320px;
  }
}

@media (max-width: 360px) {
  .loading-view {
    padding: 24px 10px;
    gap: 10px;
  }

  .loading-icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .loading-icon {
    font-size: 1.75rem;
  }

  .loading-title {
    font-size: 1rem;
  }

  .loading-message {
    font-size: 0.8rem;
    max-width: 280px;
  }

  .loading-dots .dot {
    width: 6px;
    height: 6px;
  }

  .disabled-view {
    padding: 24px 10px;
  }

  .disabled-icon {
    font-size: 2rem;
  }

  .disabled-view h3 {
    font-size: 1rem;
  }

  .disabled-view p {
    font-size: 0.8rem;
    max-width: 280px;
  }
}
</style>
